'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavigationProgress } from '@/components/layout/NavigationProgress';
import { DashboardNavigationOverlay } from '@/components/layout/DashboardPageLoading';
import { useNavigationLoadingActive } from '@/lib/use-navigate-with-loading';

type DashboardNavigationLayerProps = {
  /** Width of the fixed sidebar so the loader centers in the content pane. */
  sidebarWidth?: number;
};

/**
 * Global dashboard navigation feedback: top progress bar + centered branded overlay.
 * Mount once per dashboard shell (system, organization, legacy organizer).
 */
export function DashboardNavigationLayer({ sidebarWidth = 280 }: DashboardNavigationLayerProps) {
  const navigationLoading = useNavigationLoadingActive();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.documentElement.style.setProperty('--eh-dashboard-sidebar', `${sidebarWidth}px`);
    document.documentElement.dataset.dashboardNav = 'true';

    return () => {
      document.documentElement.style.removeProperty('--eh-dashboard-sidebar');
      delete document.documentElement.dataset.dashboardNav;
    };
  }, [sidebarWidth]);

  return (
    <>
      <NavigationProgress />
      {mounted && navigationLoading
        ? createPortal(<DashboardNavigationOverlay />, document.body)
        : null}
    </>
  );
}
